import {
  ApiError,
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  makeAuthenticatedRequest,
} from "../services/restApiService";
import type {
  AppliedOverviewFilters,
  FullOverview,
  Initiative,
  InitiativeInput,
  OverviewDepartment,
  OverviewQuery,
  OverviewSurvey,
  SchoolOverview,
  SurveyRole,
} from "../pages/Analytics/overviewTypes";
import {
  AnalyticsData,
} from "../types/types";
import { supabase } from "../supabase/supabase";

/** Release-bound response: any unexpected ON/output payload fails closed. */
export async function getAnalyticsAIStatus(organisationId: string): Promise<void> {
  const result = await makeAuthenticatedRequest<unknown>(`/analytics/ai/${encodeURIComponent(organisationId)}`);
  if (!result || typeof result !== "object" || !("status" in result) || result.status !== "disabled" || !("analysis" in result) || result.analysis !== null || !("canGenerate" in result) || result.canGenerate !== false || !("canIncludeInReport" in result) || result.canIncludeInReport !== false) {
    throw new Error("AI analysis is unavailable for this release.");
  }
}


export function overviewQueryString(query: OverviewQuery, role?: SurveyRole): string {
  const params = new URLSearchParams({ period: query.period, department: query.department });
  if (query.initiativeId) params.set("initiativeId", query.initiativeId);
  if (role) params.set("role", role);
  return params.toString();
}

// These publication reads deliberately have no process-wide client cache.
export function getSchoolOverview(organisationId: string, query: OverviewQuery): Promise<SchoolOverview> {
  return makeAuthenticatedRequest(
    `/analytics/overview/${encodeURIComponent(organisationId)}?${overviewQueryString(query)}`,
    { timeout: 60_000 },
  );
}

export function getOverviewModules(organisationId: string, query: OverviewQuery): Promise<{
  filters: AppliedOverviewFilters;
  modules: FullOverview["practice"]["modules"];
  departments: OverviewDepartment[];
  meta: FullOverview["meta"];
}> {
  return makeAuthenticatedRequest(
    `/analytics/overview/${encodeURIComponent(organisationId)}/modules?${overviewQueryString(query)}`,
    { timeout: 60_000 },
  );
}

export function getOverviewSurvey(organisationId: string, query: OverviewQuery, role: SurveyRole): Promise<{
  filters: AppliedOverviewFilters;
  survey: OverviewSurvey;
  meta: FullOverview["meta"];
}> {
  return makeAuthenticatedRequest(
    `/analytics/overview/${encodeURIComponent(organisationId)}/survey?${overviewQueryString(query, role)}`,
    { timeout: 60_000 },
  );
}

export function getGovernorsReport(organisationId: string, query: OverviewQuery): Promise<FullOverview> {
  return makeAuthenticatedRequest(
    `/analytics/governor-report/${encodeURIComponent(organisationId)}?${overviewQueryString(query)}`,
    { timeout: 60_000 },
  );
}

export function createSchoolInitiative(organisationId: string, input: InitiativeInput): Promise<Initiative> {
  return apiPost(`/analytics/initiatives/${encodeURIComponent(organisationId)}`, input);
}

export function getSchoolInitiatives(organisationId: string): Promise<Initiative[]> {
  return apiGet(`/analytics/initiatives/${encodeURIComponent(organisationId)}`);
}

export function updateSchoolInitiative(organisationId: string, id: string, input: InitiativeInput): Promise<Initiative> {
  return apiPatch(`/analytics/initiatives/${encodeURIComponent(organisationId)}/${encodeURIComponent(id)}`, input);
}

export function deleteSchoolInitiative(organisationId: string, id: string): Promise<{ id: string; deleted: true }> {
  return apiDelete(`/analytics/initiatives/${encodeURIComponent(organisationId)}/${encodeURIComponent(id)}`);
}

export async function getOwnSurveySettings(): Promise<{ actorId: string; organisationId: string; enabled: boolean; extraWaves: boolean } | null> {
  try {
    return await apiGet<{ actorId: string; organisationId: string; enabled: boolean; extraWaves: boolean }>("/surveys/me/status");
  } catch (error) {
    if (error instanceof ApiError && error.status === 404 && error.errorCode === "not_found" && error.message === "Cannot GET /surveys/me/status") return null;
    throw error;
  }
}

export function setOwnExtraSurveyWaves(organisationId: string, extraWaves: boolean): Promise<{ extraWaves: boolean }> {
  return apiPatch(`/surveys/org/${encodeURIComponent(organisationId)}/settings`, { extraWaves });
}

export async function downloadOverviewArtefact(
  organisationId: string,
  kind: "pdf" | "csv",
  query: OverviewQuery,
  signal?: AbortSignal,
): Promise<void> {
  const base = process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:3001/api";
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new ApiError(401, "AUTHENTICATION_REQUIRED", "Not authenticated");
  const response = await fetch(`${base}/analytics/${kind === "pdf" ? "governor-pack" : "export"}/${encodeURIComponent(organisationId)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ ...query, includeAnalysis: false, ...(kind === "csv" ? { exportType: "overview" } : {}) }),
    signal,
  });
  if (!response.ok) throw new ApiError(response.status, "ANALYTICS_DOWNLOAD_REFUSED", `Report download failed (${response.status}). Reload the overview and try again.`);
  const expectedType = kind === "pdf" ? "application/pdf" : "text/csv";
  if (response.headers.get("Content-Type")?.split(";")[0].trim().toLowerCase() !== expectedType) {
    throw new Error("The report response had an unexpected content type. Please try again.");
  }
  const blob = await response.blob();
  if (!blob.size) throw new Error("The report was empty. Please try again.");
  if (signal?.aborted) throw new DOMException("Download cancelled", "AbortError");
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `programme-impact-report-${organisationId.slice(0, 8)}.${kind}`;
  document.body.appendChild(anchor);
  try { anchor.click(); } finally { anchor.remove(); URL.revokeObjectURL(url); }
}

export type AnalyticsRange = "7d" | "30d" | "90d" | "custom";

export type PedaLiveTemporalBucket =
  | "focus_today"
  | "watch_this_week"
  | "longer_term_support";

export type PedaLiveSourceProduct = "learn" | "sandbox" | "play";

export interface PedaLiveLearnerEvidence {
  signalId: string;
  learnerId: string;
  learnerName: string;
  email: string;
  reason: string;
  evidence: string[];
  sourceProducts: PedaLiveSourceProduct[];
  lastDetectedAt: string;
}

export interface PedaLiveInsightCard {
  id: string;
  title: string;
  temporalBucket: PedaLiveTemporalBucket;
  temporalLabel: string;
  learnerCount: number;
  cohortMinimumMet: boolean;
  summary: string;
  suggestedSupport: string;
  sourceProducts: PedaLiveSourceProduct[];
  learners: PedaLiveLearnerEvidence[];
  whyThisAppeared: {
    inclusionCriteria: string[];
    purposeDisclosure: string;
    signalsUsed: string[];
    cohortMinimum: string;
  };
}

export interface PedaLiveSupervisorData {
  status:
    | "populated"
    | "building_baseline"
    | "empty"
    | "below_cohort_minimum"
    | "org_tier_gate";
  organisationId: string;
  sourceProducts: PedaLiveSourceProduct[];
  lastRefreshedAt: string | null;
  cards: PedaLiveInsightCard[];
  states: {
    cohortMinimum: number;
    totalLearners: number;
    evidenceRows: number;
  };
}

export interface CompetencyMetricsData {
  coreAICompetency: {
    pct: number;
    numerator: number;
    denominator: number;
    coreCoursesCount: number;
  };
  aiTopicCoverage: {
    pct: number;
    numerator: number;
    denominator: number;
    perCourseBreakdown: Array<{
      masterCourseId: string;
      courseTitle: string;
      totalLessons: number;
      currentCompletions: number;
      staleCompletions: number;
      coveragePct: number;
    }>;
  };
}

const analyticsCache = new Map<
  string,
  { data: AnalyticsData; expiresAt: number }
>();
const inflightRequests = new Map<string, Promise<AnalyticsData>>();
const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

export function clearAnalyticsCache(): void {
  analyticsCache.clear();
}

export async function getOrganisationAnalytics(
  organisationId: string,
  options?: {
    range?: AnalyticsRange;
    startDate?: string;
    endDate?: string;
  }
): Promise<AnalyticsData> {
  const cacheKey = `${organisationId}:${options?.range || "30d"}:${options?.startDate || ""}:${options?.endDate || ""}`;
  const cached = analyticsCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.data;
  }

  const inflight = inflightRequests.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const params = new URLSearchParams();
  if (options?.range) params.set("range", options.range);
  if (options?.startDate) params.set("startDate", options.startDate);
  if (options?.endDate) params.set("endDate", options.endDate);

  const queryString = params.toString();
  const suffix = queryString ? `?${queryString}` : "";
  const promise = apiGet<AnalyticsData>(
    `/analytics/dashboard/${organisationId}${suffix}`
  ).then((data) => {
    analyticsCache.set(cacheKey, { data, expiresAt: Date.now() + CACHE_TTL_MS });
    inflightRequests.delete(cacheKey);
    return data;
  }).catch((err) => {
    inflightRequests.delete(cacheKey);
    throw err;
  });

  inflightRequests.set(cacheKey, promise);
  return promise;
}

export type ExportType = "user_course_detail" | "assessment_review";

/**
 * Export analytics data as CSV download.
 * Uses fetch with blob handling since the response is a CSV file, not JSON.
 */
/**
 * Download Governor Pack PDF report.
 * Uses fetch with blob handling since the response is a PDF file, not JSON.
 */
export async function downloadGovernorPack(
  organisationId: string,
  range: "7d" | "30d" | "90d" = "30d"
): Promise<void> {
  const API_BASE_URL =
    process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:3001/api";

  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error("Not authenticated");
  }

  const response = await fetch(
    `${API_BASE_URL}/analytics/governor-pack/${organisationId}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ range }),
    }
  );

  if (!response.ok) {
    throw new Error(`Governor Pack download failed: ${response.statusText}`);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `governor-pack-${organisationId.slice(0, 8)}-${new Date().toISOString().slice(0, 10)}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function exportAnalyticsCSV(
  organisationId: string,
  exportType: ExportType,
  options?: {
    range?: AnalyticsRange;
    startDate?: string;
    endDate?: string;
    signal?: AbortSignal;
    isCurrentScope?: () => boolean;
  }
): Promise<void> {
  const API_BASE_URL =
    process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:3001/api";

  const requireCurrentScope = () => {
    if (options?.signal?.aborted || options?.isCurrentScope?.() === false) throw new DOMException("Download cancelled", "AbortError");
  };
  requireCurrentScope();
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new ApiError(401, "AUTHENTICATION_REQUIRED", "Not authenticated");
  }

  requireCurrentScope();
  const response = await fetch(
    `${API_BASE_URL}/analytics/export/${organisationId}`,
    {
      method: "POST",
      signal: options?.signal,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        exportType,
        range: options?.range,
        startDate: options?.startDate,
        endDate: options?.endDate,
      }),
    }
  );

  if (!response.ok) {
    throw new ApiError(response.status, "ANALYTICS_EXPORT_REFUSED", `Export failed: ${response.statusText}`);
  }

  // Extract filename from Content-Disposition header or use default
  const disposition = response.headers.get("Content-Disposition");
  const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
  const filename = filenameMatch?.[1] || `analytics-${exportType}.csv`;

  // Trigger download
  const blob = await response.blob();
  requireCurrentScope();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  try { requireCurrentScope(); a.click(); } finally { a.remove(); URL.revokeObjectURL(url); }
}
